import { gql, useMutation } from '@apollo/client';
import * as React from 'react';

import type {
  MarkWorkItemsReadMutation,
  MarkWorkItemsReadMutationVariables,
} from './__generated__/useMarkWorkItemsRead.gql';

const MARK_WORK_ITEMS_READ = gql`
  mutation MarkWorkItemsRead($input: MarkWorkItemsReadInputGQL!) {
    markWorkItemsRead(input: $input) {
      id
      read
      status
    }
  }
`;

/**
 * Hook for marking work items read on explicit user action — opening one, or
 * following a link from SMS.
 *
 * Batch: the input is an id array and the server
 * returns only the rows that actually flipped (already-read, unexposed, and
 * invisible ids are absent). The returned rows carry `read`, so they update in
 * place through the normalized cache — which is what flips a row's badge with
 * no refetch. Safe to fire on every open: a re-mark is not a write at all.
 *
 * Note: cache updates for this mutation are handled globally in ApolloProvider.tsx
 *
 * @returns the raw `markWorkItemsRead` mutation, a `markRead(ids)` convenience
 * wrapper, and the Apollo mutation result fields (`loading`, `error`, `data`).
 */
export const useMarkWorkItemsRead = () => {
  const [markWorkItemsRead, result] = useMutation<MarkWorkItemsReadMutation, MarkWorkItemsReadMutationVariables>(
    MARK_WORK_ITEMS_READ
  );

  const markRead = React.useCallback(
    async (ids: string[]) => {
      await markWorkItemsRead({ variables: { input: { ids } } });
    },
    [markWorkItemsRead]
  );

  return { markWorkItemsRead, markRead, ...result };
};
